import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Wallet, LogIn, LogOut, BarChart3, Newspaper, Image as ImageIcon, LayoutDashboard, LineChart, Bitcoin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";

const NavItem = ({ to, icon: Icon, label }) => (
  <NavLink
    to={to}
    data-testid={`nav-${label.toLowerCase()}`}
    className={({ isActive }) =>
      `flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all ${
        isActive ? "bg-white/10 text-white" : "text-neutral-400 hover:text-white hover:bg-white/5"
      }`
    }
  >
    <Icon size={16} />
    <span className="font-display">{label}</span>
  </NavLink>
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const [walletAddr, setWalletAddr] = useState(localStorage.getItem("wallet_addr") || "");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ stocks: [], crypto: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query || query.length < 2) { setResults({ stocks: [], crypto: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const [s, c] = await Promise.allSettled([
          client.get(`/stocks/search?q=${encodeURIComponent(query)}`),
          client.get(`/crypto/search?q=${encodeURIComponent(query)}`),
        ]);
        setResults({
          stocks: s.status === "fulfilled" ? (s.value.data.results || []).slice(0, 5) : [],
          crypto: c.status === "fulfilled" ? (c.value.data.results || []).slice(0, 5) : [],
        });
        setOpen(true);
      } catch (e) {
        console.error("search failed", e);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("MetaMask not detected. Please install MetaMask to connect a wallet.");
      return;
    }
    try {
      const accs = await window.ethereum.request({ method: "eth_requestAccounts" });
      if (accs[0]) {
        setWalletAddr(accs[0]);
        localStorage.setItem("wallet_addr", accs[0]);
      }
    } catch (e) {
      console.error("wallet connect failed", e);
    }
  };

  const goto = (path) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl border-b border-white/10 bg-black/40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4 flex items-center gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-white via-neutral-200 to-neutral-500 flex items-center justify-center">
            <span className="text-black font-bold text-xs">WB</span>
          </div>
          <span className="font-display text-xl tracking-tight">Wealth Builders</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
          <NavItem to="/markets" icon={BarChart3} label="Markets" />
          <NavItem to="/stocks" icon={LineChart} label="Stocks" />
          <NavItem to="/news" icon={Newspaper} label="News" />
          <NavItem to="/nfts" icon={ImageIcon} label="NFTs" />
          <NavItem to="/crypto" icon={Bitcoin} label="Crypto" />
        </nav>

        <div className="flex-1 max-w-md relative">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="global-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => query.length >= 2 && setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              placeholder="Search stocks & crypto…"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm placeholder-neutral-500 focus:outline-none focus:border-white/20"
            />
          </div>
          {open && (results.stocks.length || results.crypto.length) ? (
            <div className="absolute mt-2 w-full glass-strong rounded-xl overflow-hidden z-50 max-h-96 overflow-y-auto">
              {results.crypto.length > 0 && (
                <div className="p-2">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 px-2 py-1">Crypto</div>
                  {results.crypto.map((c) => (
                    <button
                      key={`c-${c.id}`}
                      data-testid={`search-crypto-${c.id}`}
                      className="w-full text-left flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5"
                      onMouseDown={() => goto(`/asset/crypto/${c.id}`)}
                    >
                      {c.thumb && <img src={c.thumb} alt="" className="w-5 h-5 rounded-full" />}
                      <span className="font-medium">{c.name}</span>
                      <span className="text-xs text-neutral-500 uppercase">{c.symbol}</span>
                    </button>
                  ))}
                </div>
              )}
              {results.stocks.length > 0 && (
                <div className="p-2 border-t border-white/5">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 px-2 py-1">Stocks</div>
                  {results.stocks.map((s) => (
                    <button
                      key={`s-${s.symbol}`}
                      data-testid={`search-stock-${s.symbol}`}
                      className="w-full text-left flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5"
                      onMouseDown={() => goto(`/asset/stock/${s.symbol}`)}
                    >
                      <div className="w-5 h-5 rounded bg-white/10 flex items-center justify-center text-[10px] font-mono">
                        {s.symbol?.slice(0, 2)}
                      </div>
                      <span className="font-medium">{s.symbol}</span>
                      <span className="text-xs text-neutral-500 truncate">{s.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="connect-wallet-button"
            onClick={connectWallet}
            className="btn-glass !py-2 !px-4 text-sm flex items-center gap-2"
            title={walletAddr || "Connect Wallet"}
          >
            <Wallet size={14} />
            <span className="hidden sm:inline font-mono-num text-xs">
              {walletAddr ? `${walletAddr.slice(0, 4)}…${walletAddr.slice(-4)}` : "Connect"}
            </span>
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              {user.picture && <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-white/10" />}
              <button data-testid="logout-button" onClick={logout} className="btn-glass !py-2 !px-3 text-sm">
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button data-testid="login-button" onClick={handleLogin} className="btn-accent !py-2 !px-4 text-sm flex items-center gap-2">
              <LogIn size={14} />
              <span className="hidden sm:inline">Sign in</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
